import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useBankAccountsInfiniteQuery,
    useDeleteBankAccountsMutation,
} from './useBankAccountsQuery';
import {useBankAccountsSearch} from './useBankAccountsSearch';
import {useBankAccountsTableColumns} from './useBankAccountsTableColumns';
import {BankAccountsFilters} from './useBankAccountsFilter';
import {BankAccount} from '../types';

/**
 * Default sort: NEWEST FIRST, on `created`.
 *
 * The key must be immutable and non-null. `created` is set when the account is
 * attached and never moves, so it satisfies both — unlike `updated`, which
 * would reorder the list under an operator who is reading it.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<BankAccount> = {
    columnAccessor: 'created',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against.
 *
 * ⚠️ `accountNumber` is NOT matched, deliberately. No query selects it, so it
 * is never populated on a loaded record — matching against it would be dead
 * code that implies the value is present. `routingNumber` is matched because it
 * identifies the bank and is what an operator reads off a statement.
 */
const matchesKeyword = (item: BankAccount, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.accountHolderName,
        item.bankName,
        item.routingNumber,
        item.country,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (
    item: BankAccount,
    filters: BankAccountsFilters,
): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof BankAccount];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listBankAccounts(filter, limit, nextToken)` takes a filter that carries the
 * customerId scope and no sort argument, so the server can neither narrow by
 * keyword nor order the result set. Everything below therefore operates on the
 * loaded window only. Sorting page 1 does not sort the dataset, and a keyword
 * matches only rows the browser has already received — the other rows were
 * never examined.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match" rather than implying it
 * searched everything.
 */
export const useBankAccounts = (
    customerId: string,
    filters: BankAccountsFilters = {},
) => {
    const query = useBankAccountsInfiniteQuery(customerId);
    const {columns} = useBankAccountsTableColumns();
    const {keyword} = useBankAccountsSearch();
    const deleteMutation = useDeleteBankAccountsMutation(customerId);

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: BankAccount[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare. Do not
    // hand-roll one; the null rule is what a hand-rolled version gets wrong.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<BankAccount>(matched, DEFAULT_SORT_STATUS);

    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
